import { useEffect } from 'react';

/**
 * Хук для закрытия компонента при нажатии клавиши Escape.
 *
 * Особенности поведения:
 * - Обработчик вешается на document.
 * - Можно управлять фазой подписки (capture / bubble).
 * - Если событие уже было обработано верхним слоем (event.defaultPrevented === true),
 *   хук игнорирует его. Это позволяет корректно работать вместе с модалками,
 *   лайтбоксами и другими оверлеями.
 *
 * Рекомендуемая архитектура:
 * - Верхние слои (Modal, Lightbox, Popup) → capture=true и вызывают preventDefault().
 * - Нижние слои (Sidebar, Drawer, Page) → capture=false (bubble) + проверка defaultPrevented.
 *
 * @param {Object} params
 * @param {boolean} params.isOpen - Флаг открытия компонента.
 *   Обработчик добавляется только если компонент открыт.
 *
 * @param {() => void} params.onCloseHandler - Функция закрытия компонента.
 *   Вызывается при нажатии Escape, если событие не было обработано ранее.
 *
 * @param {boolean} [params.capture=true] - Фаза подписки на событие keydown.
 *   true  — обработчик будет вызван в capture-фазе.
 *   false — обработчик будет вызван в bubble-фазе.
 *
 * @example
 * // Sidebar (нижний слой)
 * useCloseOnEscape({
 *   isOpen,
 *   onCloseHandler: closeSidebar,
 *   capture: false
 * });
 *
 * @example
 * // Модалка верхнего слоя
 * useCloseOnEscape({
 *   isOpen,
 *   onCloseHandler: closeModal,
 *   capture: true
 * });
 */
export const useCloseOnEscape = ({
    isOpen,
    onCloseHandler,
    capture = true,
}: {
    isOpen: boolean;
    onCloseHandler: () => void;
    capture?: boolean;
}) => {
    useEffect(() => {
        if (!isOpen || !onCloseHandler) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            if (event.defaultPrevented) return;

            if (event.key === 'Escape') {
                onCloseHandler();
            }
        };

        document.addEventListener('keydown', handleKeyDown, capture);
        return () => document.removeEventListener('keydown', handleKeyDown, capture);
    }, [isOpen, onCloseHandler, capture]);
};
